import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, Stack, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import BusinessDetailHorizontalTab from "./businessDetailHorizontalTab";
import { useDispatch } from "react-redux";
import { useMutation } from "@apollo/client";
import { UserContext } from "../../auth/auth-context";
import { setSelectedBusiness } from "../../../redux/actions/businessActions";
import { usePermissions } from "../../../hooks/usePermissions";
import { useSelectedBusiness, useSelectBusinesses } from "../../../redux/store";
import { useLocation } from "react-router-dom";
import BusinessSettings from "./businessSettings";
import { UPDATE_DEFAULT_BUSINESS } from "../../../graphql/mutations";
const BusinessListVerticalTab: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const userContext = useContext(UserContext);
  const token = userContext.providerUser;
  const { updateProviderUser } = userContext as any;
  const [isDisabled, setIsDisabled] = useState(true);
  const { canEditBusiness } = usePermissions();
  const selectedBusiness = useSelectedBusiness();
  const businesses = useSelectBusinesses();
  const [updateDefaultBusiness] = useMutation(UPDATE_DEFAULT_BUSINESS);
  const searchParams = new URLSearchParams(location.search);
  const forceNewFromState = Boolean((location.state as any)?.forceNewBusiness);
  const forceNewFromQuery = searchParams.get("new") === "1";
  const forceNewBusiness = forceNewFromState || forceNewFromQuery;

  useEffect(() => {
    const canEdit = canEditBusiness();
    if (canEdit) {
      setIsDisabled(false);
    } else {
      setIsDisabled(true);
    }
  }, [canEditBusiness]);

  useEffect(() => {
    if (forceNewBusiness) return;
    if (
      token?.defaultBusinessId &&
      (!selectedBusiness ||
        Number(selectedBusiness.businessBasic.id) !==
          Number(token.defaultBusinessId))
    ) {
      dispatch(setSelectedBusiness(token.defaultBusinessId));
    }
  }, [token?.defaultBusinessId, selectedBusiness, dispatch, forceNewBusiness]);

  const handleSelectBusinessForEdit = async (e: any) => {
    const nextId = Number(e?.target?.value || 0);
    if (!nextId) return;

    // Make selected business active immediately in UI.
    dispatch(setSelectedBusiness(nextId));
    try {
      // Keep account-level active/default business in sync.
      await updateDefaultBusiness({
        variables: { defaultBusinessId: nextId },
      });
      if (typeof updateProviderUser === "function") {
        updateProviderUser({ defaultBusinessId: nextId });
      }
    } catch (err) {
      // Selection still works locally even if default update fails.
      console.error("Failed to set default business from business profile:", err);
    }
  };

  return (
    <>
      <div className="content-header">
        <Typography
          variant="h5"
          sx={{ marginBottom: 2, textAlign: "left", marginLeft: 0 }}
        >
          Business Profile
        </Typography>
      </div>
      <Box
        sx={{
          paddingLeft: { xs: "16px", sm: "24px" }, // Add padding on small devices
          paddingRight: { xs: "16px", sm: "24px" }, // Prevent content touching the edges
          width: "100%", // Ensure full width of the container
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          sx={{ mb: 1.5 }}
        >
          {!forceNewBusiness ? (
            <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 360 } }}>
              <InputLabel id="business-profile-edit-list-label">
                Select Business To Update
              </InputLabel>
              <Select
                labelId="business-profile-edit-list-label"
                label="Select Business To Update"
                value={String(selectedBusiness?.businessBasic?.id || "")}
                onChange={handleSelectBusinessForEdit}
                disabled={!businesses?.length}
              >
                {!businesses?.length && (
                  <MenuItem value="" disabled>
                    No businesses available
                  </MenuItem>
                )}
                {businesses?.map((bus: any) => (
                  <MenuItem key={bus?.businessBasic?.id} value={String(bus?.businessBasic?.id)}>
                    {bus?.businessBasic?.name || `Business #${bus?.businessBasic?.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : <Box />}
          <Box>
            <BusinessSettings source="business-profile" />
          </Box>
        </Stack>
        <BusinessDetailHorizontalTab
          isDisabled={isDisabled || false}
          isNewBusiness={forceNewBusiness}
        />
      </Box>
    </>
  );
};

export default BusinessListVerticalTab;
